/**
 * Theming props for the Community Groups Widget.
 * These map to CSS custom properties used internally.
 */
export interface CommunityGroupsThemingProps {
  /** Primary color */
  groupsPrimaryColor?: string;
  /** Container background color */
  groupsBackgroundColor?: string;
  /** Primary text color */
  groupsTextColor?: string;
  /** Secondary text color */
  groupsSecondaryTextColor?: string;
  /** Border color */
  groupsBorderColor?: string;
  /** Card background */
  groupsCardBg?: string;
  /** Card border radius */
  groupsCardRadius?: string;
  /** Card box shadow */
  groupsCardShadow?: string;
  /** Card hover box shadow */
  groupsCardHoverShadow?: string;
  /** Card padding */
  groupsCardPadding?: string;
  /** Avatar size */
  groupsAvatarSize?: string;
  /** Avatar border radius */
  groupsAvatarRadius?: string;
  /** Group name font size */
  groupsNameSize?: string;
  /** Group name font weight */
  groupsNameWeight?: string;
  /** Meta text font size */
  groupsMetaSize?: string;
  /** Font family */
  groupsFontFamily?: string;
  /** Grid gap */
  groupsGridGap?: string;
  /** Transition duration */
  groupsTransitionDuration?: string;
  /** Hover scale factor */
  groupsHoverScale?: string;
}

/**
 * Core configuration props for the Community Groups Widget.
 */
export interface CommunityGroupsWidgetProps
  extends CommunityGroupsThemingProps {
  /** Base URL of the Legion application (required) */
  baseUrl: string;
  /** JWT auth token for authenticated features, sent via postMessage */
  authToken?: string;
  /** Number of groups to display (1-20). Default: 6 */
  count?: number;
  /** Layout mode. Default: "grid" */
  layout?: "grid" | "list";
  /** Theme preset. Default: "light" */
  theme?: "light" | "dark";
  /** Show "View all" link. Default: true */
  showViewAll?: boolean;
  /** Community URL prefix. Default: "/community" */
  communityUrl?: string;
  /** URL for the "View all" link. Default: "{communityUrl}/groups" */
  viewAllUrl?: string;
  /** Sort order. Default: "trending" */
  sortBy?: "trending" | "popular" | "recent";
  /** Widget height CSS value. Default: "auto" */
  height?: string;
  /** Widget width CSS value. Default: "100%" */
  width?: string;
  /** Additional CSS class name for the container */
  className?: string;
  /** Additional inline styles for the container */
  style?: React.CSSProperties;
  /** Callback when the widget iframe has loaded */
  onLoad?: (detail: { widget: string; count: number; theme: string }) => void;
  /** Callback when the widget iframe fails to load */
  onError?: (message: string) => void;
}

/**
 * Mapping from camelCase theming prop names to kebab-case query params.
 */
export const GROUPS_THEMING_PROP_TO_PARAM: Record<
  keyof CommunityGroupsThemingProps,
  string
> = {
  groupsPrimaryColor: "groups-primary-color",
  groupsBackgroundColor: "groups-background-color",
  groupsTextColor: "groups-text-color",
  groupsSecondaryTextColor: "groups-secondary-text-color",
  groupsBorderColor: "groups-border-color",
  groupsCardBg: "groups-card-bg",
  groupsCardRadius: "groups-card-radius",
  groupsCardShadow: "groups-card-shadow",
  groupsCardHoverShadow: "groups-card-hover-shadow",
  groupsCardPadding: "groups-card-padding",
  groupsAvatarSize: "groups-avatar-size",
  groupsAvatarRadius: "groups-avatar-radius",
  groupsNameSize: "groups-name-size",
  groupsNameWeight: "groups-name-weight",
  groupsMetaSize: "groups-meta-size",
  groupsFontFamily: "groups-font-family",
  groupsGridGap: "groups-grid-gap",
  groupsTransitionDuration: "groups-transition-duration",
  groupsHoverScale: "groups-hover-scale",
};
